import { FieldPath, UseFormSetValue } from 'react-hook-form';

import { NormalizedLoad } from 'types/Load';

export type McleodAdditionalReferencesFieldArrayPath =
  | 'pickup.additionalReferences'
  | 'consignee.additionalReferences'
  | `stops.${number}.additionalReferences`;

export function mcleodAdditionalReferencesPath(
  stopKey: string
): McleodAdditionalReferencesFieldArrayPath {
  if (isNestedMcleodRouteStopKey(stopKey)) {
    return `${stopKey}.additionalReferences` as `stops.${number}.additionalReferences`;
  }

  return `${stopKey}.additionalReferences` as
    'pickup.additionalReferences' | 'consignee.additionalReferences';
}

export type McleodStopFieldName =
  | 'externalTMSID'
  | 'name'
  | 'addressLine1'
  | 'addressLine2'
  | 'city'
  | 'state'
  | 'zipCode'
  | 'country'
  | 'timezone'
  | 'contact'
  | 'phone'
  | 'refNumber'
  | 'apptRequired'
  | 'apptStartTime'
  | 'apptEndTime'
  | 'additionalReferences'
  | 'hotComment'
  | 'billingComment';

export function isNestedMcleodRouteStopKey(stopKey: string): boolean {
  return stopKey.startsWith('stops.');
}

export function mcleodStopFieldPath(
  stopKey: string,
  field: McleodStopFieldName
): FieldPath<NormalizedLoad> {
  if (isNestedMcleodRouteStopKey(stopKey)) {
    if (
      field === 'externalTMSID' ||
      field === 'contact' ||
      field === 'phone' ||
      field === 'refNumber' ||
      field === 'apptRequired' ||
      field === 'apptStartTime' ||
      field === 'apptEndTime' ||
      field === 'additionalReferences' ||
      field === 'hotComment' ||
      field === 'billingComment' ||
      field === 'timezone'
    ) {
      return `${stopKey}.${field}` as FieldPath<NormalizedLoad>;
    }

    if (field === 'zipCode') {
      return `${stopKey}.address.zip` as FieldPath<NormalizedLoad>;
    }

    if (field === 'country') {
      return `${stopKey}.address.country` as FieldPath<NormalizedLoad>;
    }

    return `${stopKey}.address.${field}` as FieldPath<NormalizedLoad>;
  }

  return `${stopKey}.${field}` as FieldPath<NormalizedLoad>;
}

type McleodStopLocationView = {
  name?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
};

export function getMcleodStopLocationView(
  stopKey: string,
  watchedStop: unknown
): McleodStopLocationView {
  if (!watchedStop || typeof watchedStop !== 'object') {
    return {};
  }

  if (isNestedMcleodRouteStopKey(stopKey)) {
    const stop = watchedStop as {
      address?: {
        name?: string;
        addressLine1?: string;
        addressLine2?: string;
        city?: string;
        state?: string;
        zip?: string;
      };
    };

    return {
      name: stop.address?.name,
      addressLine1: stop.address?.addressLine1,
      addressLine2: stop.address?.addressLine2,
      city: stop.address?.city,
      state: stop.address?.state,
      zipCode: stop.address?.zip,
    };
  }

  const stop = watchedStop as McleodStopLocationView;
  return {
    name: stop.name,
    addressLine1: stop.addressLine1,
    addressLine2: stop.addressLine2,
    city: stop.city,
    state: stop.state,
    zipCode: stop.zipCode,
  };
}

export function applySelectedLocationToMcleodStop(
  stopKey: string,
  selectedLoc: Record<string, unknown>,
  setValue: UseFormSetValue<NormalizedLoad>
): void {
  Object.entries(selectedLoc).forEach(([key, value]) => {
    if (key === 'contact' || key === 'phone') {
      return;
    }

    if (isNestedMcleodRouteStopKey(stopKey)) {
      if (key === 'zipCode') {
        setValue(mcleodStopFieldPath(stopKey, 'zipCode'), value as string, {
          shouldDirty: true,
        });
        return;
      }

      if (
        key === 'name' ||
        key === 'addressLine1' ||
        key === 'addressLine2' ||
        key === 'city' ||
        key === 'state' ||
        key === 'country' ||
        key === 'timezone'
      ) {
        setValue(
          mcleodStopFieldPath(stopKey, key as McleodStopFieldName),
          value as string,
          { shouldDirty: true }
        );
        return;
      }

      if (key === 'externalTMSID' || key === 'apptRequired') {
        setValue(
          mcleodStopFieldPath(stopKey, key as McleodStopFieldName),
          value as string | boolean,
          { shouldDirty: true }
        );
      }

      return;
    }

    setValue(
      `${stopKey}.${key}` as FieldPath<NormalizedLoad>,
      value as string,
      { shouldDirty: true }
    );
  });
}
